import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useStudentReviewContext } from "../context/StudentReviewTestContext";
import { StudentReviewResultLoadStatuses } from "../enums/studentReview";
import { getIsLegacyTest } from "../helpers/LegacyTest";
import { buildStudentResult, type StudentResult } from "../helpers/StudentResultModel";

interface UseStudentResultValue {
    result: StudentResult;
    /** True once the result fetch has failed — the only unsettled state the access guard lets through. */
    hasLoadingError: boolean;
    /**
     * The classroom to return to, or "" for a legacy share link — those predate Enhanced Classes and
     * are opened by anonymous link, so there is no classroom behind them to go back to.
     */
    classroomId: string;
}

/**
 * Feeds the student result pages (list and exercise drill-in): starts the shared-result fetch for
 * the route's shareId and builds the render model from the loaded data.
 */
const useStudentResult = (): UseStudentResultValue => {
    const { shareId } = useParams<{ shareId: string }>();
    const { setShareId, resultData, studentTest, resultLoadStatus } = useStudentReviewContext();

    useEffect(() => {
        if (shareId) {
            setShareId(shareId);
        }
    }, [shareId, setShareId]);

    const result = useMemo(() => buildStudentResult(resultData, studentTest), [resultData, studentTest]);

    return {
        result,
        hasLoadingError: resultLoadStatus === StudentReviewResultLoadStatuses.ERROR,
        classroomId: getIsLegacyTest(studentTest) ? "" : studentTest.groupId || "",
    };
};

export default useStudentResult;
